import React from 'react';
import {Box, Typography} from '@mui/material';
import {BuilderPot, BuilderPot_dentistChildren} from '../../../schema-types/BuilderPot';
import {PlanAccordionShell} from '../CreateBuilder/BuilderSourceDestination/PlanAccordionShell';
import {PlanSelector} from '../CreateBuilder/BuilderSourceDestination/PlanSelector';
import {PlanValidationIcon} from '../CreateBuilder/BuilderSourceDestination/PlanValidationIcon';
import {usePlanValidationLevel} from '../CreateBuilder/BuilderSourceDestination/usePlanValidationLevel';
import {
  getSummary,
  getSummaryParts,
} from '../CreateBuilder/BuilderSourceDestination/builderNaming';
import {ParentPlanRow} from '../CreateBuilder/BuilderSourceDestination/ParentPlanRow';
import {WithdrawalSourceSubplan} from './WithdrawalSourceSubplan';
import {WithdrawalDetails} from './BuilderWithdrawalSources';
import {getCurrencyFloat} from '../../../utils/currencyFormatter';

interface WithdrawalSourceParentProps {
  pot: BuilderPot;
  withdrawSourceId?: string;
  greyed?: boolean;
  sameReview?: boolean;
  handleChange: (id: string) => void;
}

export const WithdrawalSourceParent: React.FC<WithdrawalSourceParentProps> = ({
  pot,
  withdrawSourceId,
  greyed,
  sameReview,
  handleChange,
}) => {
  const dentistChildren: BuilderPot_dentistChildren[] = pot.dentistChildren ?? [];
  const hasChildren = dentistChildren.length > 0;
  const isLeafSelected = !hasChildren && withdrawSourceId === pot.id;
  const childSelected =
    hasChildren && dentistChildren.some((c) => c.id === withdrawSourceId);
  const activeFormikKey = isLeafSelected || childSelected ? 'withdraw.source' : undefined;
  const {bgClass} = usePlanValidationLevel(activeFormikKey);

  if (hasChildren) {
    const {label, currentValue} = getSummaryParts(pot);
    return (
      <ParentPlanRow
        pot={pot}
        isSelected={childSelected}
        sameReview={sameReview}
        validationLevel={bgClass}
        summaryChildren={
          <Box sx={{display: 'flex', alignItems: 'center', flex: 1}}>
            <Typography variant="body1" sx={{fontWeight: 600}}>
              {label}
            </Typography>
            {currentValue && (
              <Typography
                variant="body2"
                sx={{fontWeight: 600, ml: 'auto', whiteSpace: 'nowrap'}}
              >
                {currentValue}
              </Typography>
            )}
          </Box>
        }
        summaryTrailing={
          childSelected && activeFormikKey ? (
            <PlanValidationIcon formikKeyPrefix={activeFormikKey} />
          ) : undefined
        }
      >
        {dentistChildren.map((child) => (
          <WithdrawalSourceSubplan
            key={child.id}
            subPlan={child}
            isSelected={withdrawSourceId === child.id}
            handleChange={() => handleChange(child.id)}
          />
        ))}
      </ParentPlanRow>
    );
  }

  return (
    <ParentPlanRow pot={pot} isSelected={isLeafSelected} sameReview={sameReview}>
      <PlanAccordionShell
        expanded={isLeafSelected}
        isSelected={isLeafSelected}
        isSoleParent
        validationLevel={bgClass}
        greyed={greyed}
        sameReview={sameReview}
        summaryChildren={
          <>
            <PlanSelector
              mode="single"
              id={pot.id}
              isSelected={isLeafSelected}
              handleChange={() => handleChange(pot.id)}
            />
            {getSummary(pot)}
          </>
        }
        summaryTrailing={
          activeFormikKey ? (
            <PlanValidationIcon formikKeyPrefix={activeFormikKey} />
          ) : undefined
        }
      >
        <WithdrawalDetails pot={pot} maxAmount={getCurrencyFloat(pot.totalValue)} />
      </PlanAccordionShell>
    </ParentPlanRow>
  );
};
